import React from 'react';
import { reportClientError } from '../../lib/rum';

interface ErrorBoundaryProps {
  children: React.ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: React.ErrorInfo | null;
}

export default class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(_error: Error): Partial<ErrorBoundaryState> {
    return { hasError: true };
  }

  override componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    this.setState({ error, errorInfo });
    // Log error to console. Sentry's ErrorBoundary (wrapping this one in App)
    // captures structured reports; this console.error keeps local DX intact.
    console.error('ErrorBoundary caught:', error, errorInfo);
    // Консоль Telegram-webview невидима — шлём краш в логи прода (mobile-gw).
    reportClientError({
      message: String(error),
      stack: error.stack,
      componentStack: errorInfo.componentStack ?? undefined,
    });
  }

  handleReload = (): void => {
    window.location.reload();
  };

  handleReset = (): void => {
    this.setState({ hasError: false, error: null, errorInfo: null });
  };

  override render(): React.ReactNode {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-zinc-950 text-zinc-100 flex items-center justify-center p-4">
          <div className="max-w-md w-full bg-zinc-900 rounded-xl border border-zinc-800 p-6 text-center">
            <div className="text-4xl mb-4">
              <span role="img" aria-label="error">
                !
              </span>
            </div>
            <h1 className="text-xl font-bold text-red-400 mb-2">Что-то сломалось</h1>
            <p className="text-zinc-400 mb-6">
              Уже записал, что случилось. Попробуй ещё раз — обычно помогает.
            </p>

            {this.state.error && (
              <div className="mb-6 text-left">
                <p className="text-xs text-zinc-500 mb-1">Error:</p>
                <pre className="bg-zinc-800 rounded p-3 text-xs text-red-400 overflow-x-auto">
                  {this.state.error.toString()}
                </pre>
              </div>
            )}

            {this.state.errorInfo?.componentStack && (
              <div className="mb-6 text-left">
                <p className="text-xs text-zinc-500 mb-1">Component:</p>
                {/* Верхушка component stack прямо на экране: скриншот фолбэка
                    сам по себе называет виновный компонент (webview-консоль
                    невидима, а до логов дело может не дойти). */}
                <pre className="bg-zinc-800 rounded p-3 text-xs text-zinc-400 overflow-x-auto max-h-40">
                  {this.state.errorInfo.componentStack.trim().split('\n').slice(0, 8).join('\n')}
                </pre>
              </div>
            )}

            <div className="flex gap-3 justify-center">
              <button
                onClick={this.handleReset}
                className="px-4 py-2 bg-zinc-700 hover:bg-zinc-600 rounded-lg text-sm font-medium transition-colors"
              >
                Попробовать снова
              </button>
              <button
                onClick={this.handleReload}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-500 rounded-lg text-sm font-medium transition-colors"
              >
                Перезагрузить
              </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
